import { DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';

/**
 * Controls when the site-wide email pop-up (sections/email-popup.liquid)
 * opens, and remembers whether a visitor already dismissed or subscribed so
 * it doesn't nag them again within the cooldown window.
 *
 * Storage: localStorage only (no cookies) - a single timestamp under
 * STORAGE_KEY. If `Date.now() < storedTimestamp`, the pop-up stays hidden.
 * A dismiss without subscribing stores `now + cooldownDays`. A successful
 * subscribe stores a long 365-day value instead.
 *
 * Collision handling with other dialogs (search, cart drawer) is
 * bidirectional:
 *  - Before the pop-up opens itself (delay timer or exit-intent), it checks
 *    whether another dialog is already open and defers instead of stacking.
 *  - If another dialog opens WHILE the pop-up is already showing, the
 *    pop-up closes itself immediately, since it reacts to the other
 *    dialog's own open event rather than the other way around.
 */
(() => {
  const STORAGE_KEY = 'sterling:email-popup:hidden-until';
  const SESSION_SHOWN_KEY = 'sterling:email-popup:shown-this-session';
  const SUBSCRIBED_COOLDOWN_DAYS = 365;

  const dialogEl = document.getElementById('email-popup-modal');
  const config = document.getElementById('email-popup-config');
  if (!dialogEl || !config) return;

  const delaySeconds = Number(config.dataset.delaySeconds) || 0;
  const exitIntentEnabled = config.dataset.exitIntent === 'true';
  const cooldownDays = Number(config.dataset.cooldownDays) || 30;
  const postedSuccessfully = config.dataset.postedSuccessfully === 'true';

  const daysToMs = (days) => days * 24 * 60 * 60 * 1000;

  const isCoolingDown = () => {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const hiddenUntil = Number(raw);
    if (Number.isNaN(hiddenUntil)) return false;
    return Date.now() < hiddenUntil;
  };

  const setCooldown = (days) => {
    window.localStorage.setItem(STORAGE_KEY, String(Date.now() + daysToMs(days)));
  };

  /** @returns {import('@theme/dialog').DialogComponent | null} */
  const getDialogComponent = () =>
    /** @type {import('@theme/dialog').DialogComponent | null} */ (dialogEl);

  /**
   * True if some other dialog (search, cart drawer, etc.) is already open.
   * Prevents the pop-up from stacking on top of a dialog the visitor is
   * actively using - e.g. the delay timer firing while they have the cart
   * drawer open.
   */
  const isAnotherDialogOpen = () => {
    const openDialogs = document.querySelectorAll('dialog[open]');
    for (const openDialog of openDialogs) {
      if (!dialogEl.contains(openDialog)) return true;
    }
    return false;
  };

  const openPopup = () => {
    if (window.sessionStorage.getItem(SESSION_SHOWN_KEY)) return;
    const dialog = getDialogComponent();
    if (!dialog || typeof (/** @type {any} */ (dialog).showDialog) !== 'function') return;

    window.sessionStorage.setItem(SESSION_SHOWN_KEY, 'true');
    /** @type {any} */ (dialog).showDialog();
  };

  /**
   * Defers opening while another dialog is open, retrying briefly instead
   * of stacking on top of it. Gives up quietly after ~20s so this never
   * turns into a persistent nag once the visitor moves on.
   * @param {number} [attemptsLeft]
   */
  const tryOpenPopup = (attemptsLeft = 40) => {
    if (window.sessionStorage.getItem(SESSION_SHOWN_KEY)) return;

    if (isAnotherDialogOpen()) {
      if (attemptsLeft <= 0) return;
      window.setTimeout(() => tryOpenPopup(attemptsLeft - 1), 500);
      return;
    }

    openPopup();
  };

  /**
   * Closes the pop-up immediately if it happens to be open. Wired to fire
   * when another dialog opens, so this is the half of the collision fix
   * that isn't covered by isAnotherDialogOpen() (which only guards the
   * pop-up's own opening, not other dialogs opening on top of it).
   */
  const closePopupIfOpen = () => {
    const dialog = getDialogComponent();
    const nativeDialog = dialogEl.querySelector('dialog');
    if (dialog && nativeDialog?.open && typeof (/** @type {any} */ (dialog).closeDialog) === 'function') {
      /** @type {any} */ (dialog).closeDialog();
    }
  };

  // Search modal and the cart drawer are both dialog-component instances
  // like this one; DialogOpenEvent doesn't bubble, so each is listened to
  // directly rather than via a single delegated document listener.
  document.getElementById('search-modal')?.addEventListener(DialogOpenEvent.eventName, closePopupIfOpen);
  document.querySelector('cart-drawer-component')?.addEventListener(DialogOpenEvent.eventName, closePopupIfOpen);

  // The page just reloaded after a successful subscribe (native form POST).
  // Show the thank-you state immediately and lock the cooldown long-term -
  // this visitor doesn't need the offer again.
  if (postedSuccessfully) {
    setCooldown(SUBSCRIBED_COOLDOWN_DAYS);
    window.sessionStorage.setItem(SESSION_SHOWN_KEY, 'true');
    const dialog = getDialogComponent();
    if (dialog && typeof (/** @type {any} */ (dialog).showDialog) === 'function') {
      /** @type {any} */ (dialog).showDialog();
    }
  } else if (!isCoolingDown()) {
    if (delaySeconds >= 0) {
      window.setTimeout(() => tryOpenPopup(), delaySeconds * 1000);
    }

    if (exitIntentEnabled && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      // Guard against a mouseleave firing spuriously right at page load,
      // before any real cursor position is established. Exit-intent
      // cannot fire within the first EXIT_INTENT_ARM_DELAY_MS of load, and
      // additionally requires a genuine in-page mousemove to have been
      // observed first.
      const EXIT_INTENT_ARM_DELAY_MS = 1500;
      const pageLoadedAt = performance.now();
      let hasMovedMouseInPage = false;

      const handleMouseMove = () => {
        hasMovedMouseInPage = true;
      };
      document.addEventListener('mousemove', handleMouseMove, { once: true });

      /** @param {MouseEvent} event */
      const handleMouseLeave = (event) => {
        if (performance.now() - pageLoadedAt < EXIT_INTENT_ARM_DELAY_MS) return;
        if (!hasMovedMouseInPage) return;
        if (event.clientY > 0) return;
        tryOpenPopup();
        document.removeEventListener('mouseleave', handleMouseLeave);
        document.removeEventListener('mousemove', handleMouseMove);
      };
      document.addEventListener('mouseleave', handleMouseLeave);
    }
  }

  // Any close that wasn't triggered by a successful subscribe counts as a
  // dismissal - start the shorter cooldown window. This also fires when
  // closePopupIfOpen() closes it due to another dialog opening, which is
  // the right call: the visitor didn't see a full, uninterrupted offer,
  // but re-showing it the moment they close the other dialog would be
  // worse UX than just deferring to the next session.
  dialogEl.addEventListener(DialogCloseEvent.eventName, () => {
    if (postedSuccessfully) return;
    if (isCoolingDown()) return;
    setCooldown(cooldownDays);
  });
})();
